import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CREDENTIAL_STATUS_CHANGED_SSE_EVENT,
  CREDENTIALS_VERIFICATION_UPDATED_SSE_EVENT,
} from '@terra/shared-contracts-applications';
import { mergeCredentialVerificationUpdate } from '@terra/shared-utils';
import { useSSE } from '../../../app/providers/SseProvider';
import { useInvalidateApplicationCredentials } from '../../../hooks/api/useDecryptedApplicationCredentials';
import {
  getDecryptedCredentialsQueryKey,
  toRestVerification,
  type BackendCredential,
  type RestCredentialVerification,
} from './applicationCredential.transforms';

type CredentialsCache = { credentials: BackendCredential[] } | undefined;

type CredentialVerification = RestCredentialVerification;

const mergeVerification = ({
  existingArtifacts,
  verification,
}: {
  existingArtifacts: CredentialVerification['artifacts'];
  verification: CredentialVerification;
}) =>
  mergeCredentialVerificationUpdate<CredentialVerification>({
    existingArtifacts:
      verification.status === 'VERIFIED' ? [] : existingArtifacts,
    verification,
    preserveDecryptedArtifactsOnVerified: true,
  });

export function useTargetCredentialsSse({
  applicationId,
}: {
  applicationId?: string;
}) {
  const queryClient = useQueryClient();
  const { subscribe } = useSSE();
  const invalidateApplicationCredentials =
    useInvalidateApplicationCredentials();

  useEffect(() => {
    if (!applicationId) return;
    const queryKey = getDecryptedCredentialsQueryKey(applicationId);
    const unsubscribeVerification = subscribe(
      CREDENTIALS_VERIFICATION_UPDATED_SSE_EVENT,
      (eventData) => {
        try {
          if (eventData.applicationId !== applicationId) return;
          const verification = toRestVerification(eventData.verification);

          queryClient.setQueryData(queryKey, (oldData: CredentialsCache) => {
            if (!oldData?.credentials) return oldData;

            return {
              ...oldData,
              credentials: oldData.credentials.map((credential) =>
                credential.id === eventData.credentialId
                  ? {
                      ...credential,
                      verification: mergeVerification({
                        existingArtifacts: credential.verification?.artifacts,
                        verification,
                      }),
                    }
                  : credential,
              ),
            };
          });

          if (verification.status === 'VERIFIED') {
            void invalidateApplicationCredentials(applicationId);
          }
        } catch (error) {
          console.error('Error processing credentials SSE event:', error);
        }
      },
    );
    const unsubscribeStatus = subscribe(
      CREDENTIAL_STATUS_CHANGED_SSE_EVENT,
      (eventData) => {
        try {
          if (eventData.applicationId !== applicationId) return;
          const applyStatusUpdate = <T extends { id: string }>(
            credential: T,
          ): T =>
            credential.id === eventData.credentialId
              ? { ...credential, isDisabled: eventData.isDisabled }
              : credential;

          queryClient.setQueryData(queryKey, (oldData: CredentialsCache) => {
            if (!oldData?.credentials) return oldData;

            return {
              ...oldData,
              credentials: oldData.credentials.map(applyStatusUpdate),
            };
          });
        } catch (error) {
          console.error('Error processing credentials SSE event:', error);
        }
      },
    );

    return () => {
      unsubscribeVerification();
      unsubscribeStatus();
    };
  }, [subscribe, applicationId, queryClient, invalidateApplicationCredentials]);

  return { subscribe };
}
